
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/context/auth';
import { useToast } from '@/hooks/use-toast';
import { TeacherSalaryVerification } from '@/types/salaryVerification';

export function useTeacherVerification(month: number, year: number) {
  const { user } = useAuth();
  const { toast } = useToast();
  const [verification, setVerification] = useState<TeacherSalaryVerification | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [teacherProfile, setTeacherProfile] = useState<any>(null);

  // Get teacher profile
  useEffect(() => {
    const fetchTeacherProfile = async () => {
      if (!user) return;
      
      const { data, error } = await supabase
        .from('profiles')
        .select('teacher_id, user_type')
        .eq('id', user.id)
        .single();

      if (error) {
        console.error('Error fetching teacher profile:', error);
        return;
      }

      setTeacherProfile(data);
    };

    fetchTeacherProfile();
  }, [user]);

  // Fetch teacher verification status
  useEffect(() => {
    const fetchVerification = async () => {
      if (!teacherProfile?.teacher_id) return;

      setIsLoading(true);
      
      const { data, error } = await supabase
        .from('teacher_salary_verifications')
        .select('*')
        .eq('teacher_id', teacherProfile.teacher_id)
        .eq('month', month)
        .eq('year', year)
        .maybeSingle();

      if (error) {
        console.error('Error fetching verification:', error);
      } else {
        setVerification(data);
      }

      setIsLoading(false);
    };

    if (teacherProfile?.teacher_id) {
      fetchVerification();
    }
  }, [teacherProfile, month, year]);

  const verifyTeacher = async (verificationData: any) => {
    if (!teacherProfile?.teacher_id) return false;

    const verificationRecord = {
      teacher_id: teacherProfile.teacher_id,
      month,
      year,
      is_verified: true,
      verification_data: verificationData,
      verified_at: new Date().toISOString()
    };

    const { data, error } = await supabase
      .from('teacher_salary_verifications')
      .upsert(verificationRecord)
      .select()
      .single();

    if (error) {
      toast({
        title: 'เกิดข้อผิดพลาด',
        description: 'ไม่สามารถยืนยันข้อมูลได้',
        variant: 'destructive'
      });
      return false;
    }

    setVerification(data);
    toast({
      title: 'ยืนยันสำเร็จ',
      description: 'ข้อมูลของคุณได้รับการยืนยันแล้ว'
    });
    return true;
  };

  return {
    verification,
    teacherProfile,
    isLoading,
    verifyTeacher
  };
}
